import { useCallback, useEffect, useMemo, useState } from 'react';
import employeesApi from 'Api/axios/employees-api';
import type { AssignableCourseI } from 'types';

/**
 * Cursos opcionales que el empleado podría tomar y todavía no tiene.
 *
 * El filtrado y la paginación son en memoria, igual que en
 * `useAssignableCourses` y por la misma razón: son decenas de cursos, no miles,
 * y pedir una página al servidor por cada clic costaría más de lo que ahorra.
 */
const useAvailableOptionalCourses = (employeeId: string) => {
  const [all, setAll] = useState<AssignableCourseI[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearchState] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const refetch = useCallback(async () => {
    if (!employeeId) return;

    setLoading(true);
    const response = await employeesApi.readAvailableOptionalCourses(employeeId);
    setAll(response.data ?? []);
    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    if (!needle) return all;

    return all.filter(course => course.name.toLocaleLowerCase().includes(needle));
  }, [all, search]);

  const setSearch = (value: string) => {
    setSearchState(value);
    setPage(1);
  };

  return {
    /**
     * El catálogo entero, sin filtrar ni paginar.
     *
     * Lo consume la tabla del plan, que mete estos cursos en la pestaña
     * "Opcionales" y aplica su propia búsqueda y su propio paginador. Pasarle la
     * página ya cortada haría que el paginador contara mal.
     */
    allCourses: all,
    courses: filtered.slice((page - 1) * pageSize, page * pageSize),
    loading,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalItems: filtered.length,
    totalPages: Math.ceil(filtered.length / pageSize),
    refetch,
  };
};

export default useAvailableOptionalCourses;
